/*
 * Maddox Gallery — BookX intent gating
 * PREVIEW-ONLY TEST CANDIDATE
 *
 * Required Shopify preview setting:
 *   Disable the BookX Theme App Embed ONLY on this unpublished preview theme.
 *
 * Behaviour:
 *   - Ordinary storefront visits: BookX/reCAPTCHA do not initialize.
 *   - Existing BookX URLs still work.
 *   - On an existing same-origin BookX CTA click, the booking query is pushed
 *     into the current URL and BookX is initialized immediately.
 *   - Direct visits containing BookX query parameters initialize BookX immediately.
 *   - If dynamic initialization fails, the browser falls back to normal navigation.
 *
 * Do not use this together with the enabled BookX Theme App Embed: doing so
 * defeats the performance test and may initialize BookX twice.
 */
(function () {
  'use strict';

  var BOOKX_SRC =
    'https://cdn.shopify.com/extensions/01a06b2a-c160-756b-b9fa-ae4a6aba350c/bookeasy-182/assets/bookeasy-widget.js';

  var BOOKX_URL_PATTERN = /(?:[?&](?:bookeasy-type|bookeasy-id)=)|\/apps\/bookeasy(?:\/|$)/i;
  var SCRIPT_PATTERN = /\/bookeasy-[^/]+\/assets\/bookeasy-widget\.js(?:[?#]|$)/i;

  var loadPromise = null;
  var fallbackTimer = null;

  function isBookXUrl(value) {
    try {
      var url = new URL(value, window.location.href);
      return BOOKX_URL_PATTERN.test(url.pathname + url.search);
    } catch (error) {
      return false;
    }
  }

  function existingBookXScript() {
    var scripts = document.getElementsByTagName('script');
    for (var i = 0; i < scripts.length; i += 1) {
      var src = scripts[i].src || '';
      if (SCRIPT_PATTERN.test(src) || src === BOOKX_SRC) return scripts[i];
    }
    return null;
  }

  function clearFallback() {
    if (fallbackTimer) {
      window.clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
  }

  function loadBookX() {
    if (loadPromise) return loadPromise;

    loadPromise = new Promise(function (resolve, reject) {
      var existing = existingBookXScript();
      if (existing) {
        resolve(existing);
        return;
      }

      var script = document.createElement('script');
      script.src = BOOKX_SRC;
      script.async = true;
      script.setAttribute('data-maddox-bookx-intent', 'true');

      script.onload = function () {
        document.documentElement.setAttribute('data-bookx-intent-loaded', 'true');
        clearFallback();
        resolve(script);
      };

      script.onerror = function () {
        loadPromise = null;
        reject(new Error('BookX intent loader failed to load the current BookX widget bundle.'));
      };

      document.head.appendChild(script);
    });

    return loadPromise;
  }

  function navigateFallback(url) {
    clearFallback();
    fallbackTimer = window.setTimeout(function () {
      window.location.assign(url.href);
    }, 5000);
  }

  function activateBookingUrl(url, clickedLink) {
    /*
     * Preserve BookX's own URL contract. The widget currently reads
     * bookeasy-type/bookeasy-id from the URL, so expose those parameters
     * before loading its bundle.
     */
    try {
      if (url.origin === window.location.origin && url.pathname === window.location.pathname) {
        window.history.pushState(
          { maddoxBookXIntent: true },
          '',
          url.pathname + url.search + url.hash
        );
      } else {
        /*
         * For a BookX URL targeting a different storefront path, navigation is
         * the safest behaviour. The destination page will see the BookX query
         * on startup and initialize via the direct-visit branch below.
         */
        window.location.assign(url.href);
        return;
      }
    } catch (error) {
      window.location.assign(url.href);
      return;
    }

    navigateFallback(url);

    loadBookX().then(function () {
      /*
       * BookX initializes itself from the current URL. We intentionally do not
       * call private/undocumented BookX methods or touch grecaptcha.
       */
      clearFallback();
      document.dispatchEvent(new CustomEvent('maddox:bookx-intent-loaded', {
        detail: { href: url.href }
      }));
    }).catch(function () {
      window.location.assign(url.href);
    });
  }

  function bookingLinkFromEvent(event) {
    if (!event || event.defaultPrevented) return null;
    if (event.button !== undefined && event.button !== 0) return null;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;

    var target = event.target;
    if (!target || !target.closest) return null;

    var link = target.closest('a[href]');
    if (!link || !isBookXUrl(link.href)) return null;

    return link;
  }

  document.addEventListener('click', function (event) {
    var link = bookingLinkFromEvent(event);
    if (!link) return;

    var url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (error) {
      return;
    }

    /*
     * External/new-tab/download links keep native browser behaviour.
     */
    if (
      url.origin !== window.location.origin ||
      link.target === '_blank' ||
      link.hasAttribute('download')
    ) {
      return;
    }

    event.preventDefault();
    activateBookingUrl(url, link);
  }, true);

  /*
   * Direct booking URLs: initialize immediately because booking intent is
   * already explicit. Ordinary URLs do nothing.
   */
  if (isBookXUrl(window.location.href)) {
    loadBookX().catch(function () {
      /*
       * No blocking overlay and no reCAPTCHA manipulation: native page remains
       * usable if BookX itself cannot be fetched.
       */
      document.documentElement.setAttribute('data-bookx-intent-error', 'true');
    });
  }

  /*
   * Handle browser back/forward navigation into a BookX query URL.
   */
  window.addEventListener('popstate', function () {
    if (isBookXUrl(window.location.href)) {
      loadBookX().catch(function () {});
    }
  });

  window.MaddoxBookXIntent = {
    load: loadBookX,
    isBookingUrl: isBookXUrl,
    source: BOOKX_SRC
  };
}());
